import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';

import { buildHumanLearningModel } from './lidHumanLearning';
import {
  getLearningCopy,
  type LearningVisualKind,
} from './lidLearning';

interface Props {
  language: string;
  questionKey: string;
  question: string;
  translatedQuestion?: string | null;
  translationDirection?: 'ltr' | 'rtl';
  selectedKey?: string | null;
  selectedAnswer?: string | null;
  translatedSelectedAnswer?: string | null;
  correctKey?: string | null;
  correctAnswer?: string | null;
  translatedCorrectAnswer?: string | null;
  correct?: boolean;
  attemptId?: string | null;
  correctLabel: string;
  incorrectLabel: string;
  answerConnection?: LearningBridgeResult | null;
  // Options for enhanced learning (shared terms detection)
  options?: string[];
}

export interface LearningBridgeResult {
  relation: string;
  question_focus: string;
  answer_evidence: string;
  complete_fact: string;
  translated_question_focus: string;
  translated_answer_evidence: string;
  translated_complete_fact: string;
  reason: string;
}

function visualContent(kind: LearningVisualKind) {
  if (kind === 'rights') {
    return <path d="M48 17v44M29 27h38M34 27 23 49h22ZM62 27 51 49h22ZM31 63h34" />;
  }
  if (kind === 'institution') {
    return <path d="m17 31 31-16 31 16ZM22 34h52M27 38v20M40 38v20M56 38v20M69 38v20M18 62h60" />;
  }
  if (kind === 'history') {
    return <><circle cx="48" cy="37" r="22" /><path d="M48 24v14l11 7M20 64h56" /></>;
  }
  if (kind === 'society') {
    return <><circle cx="48" cy="25" r="9" /><circle cx="25" cy="34" r="7" /><circle cx="71" cy="34" r="7" /><path d="M29 63c1-15 8-22 19-22s18 7 19 22M12 62c1-11 5-17 13-17M84 62c-1-11-5-17-13-17" /></>;
  }
  if (kind === 'state') {
    return <><path d="M48 66c13-17 21-27 21-37a21 21 0 1 0-42 0c0 10 8 20 21 37Z" /><circle cx="48" cy="29" r="8" /></>;
  }
  if (kind === 'answer') {
    return <><rect x="18" y="14" width="60" height="48" rx="10" /><path d="m27 38 7 7 14-17M54 31h14M54 41h11M54 51h8" /></>;
  }
  return <><circle cx="48" cy="37" r="23" /><path d="M48 17v40M28 37h40" /></>;
}

function MeaningVisual({ kind }: { kind: LearningVisualKind }) {
  return (
    <div className={`lid-human-visual is-${kind}`} aria-hidden="true">
      <svg viewBox="0 0 96 78" focusable="false">
        {visualContent(kind)}
      </svg>
    </div>
  );
}

function patternKey(
  relation: string,
  visual: string,
  signal: string,
): string {
  const normalizedSignal =
    signal
      .toLocaleLowerCase('de-DE')
      .replace(/[^a-zäöüß0-9]+/giu, '-')
      .replace(/^-|-$/g, '');

  return [
    relation,
    visual,
    normalizedSignal || 'general',
  ].join(':');
}


function readPatternMastery(
  key: string,
): number {
  try {
    return Number(
      localStorage.getItem(
        `lid.human-learning.pattern-mastery.${key}`
      )
      || 0
    );
  } catch {
    return 0;
  }
}

export function LiDLearningBridge({
  language,
  questionKey,
  question,
  translatedQuestion,
  translationDirection = 'ltr',
  selectedKey,
  selectedAnswer,
  translatedSelectedAnswer,
  correctKey,
  correctAnswer,
  translatedCorrectAnswer,
  correct,
  attemptId,
  correctLabel,
  incorrectLabel,
  answerConnection,
  options = [],
}: Props) {
  const copy = getLearningCopy(language);

  const model = useMemo(
    () => buildHumanLearningModel(
      question,
      translatedQuestion,
      language,
      options,
    ),
    [
      language,
      options,
      question,
      translatedQuestion,
    ],
  );
  const masteryKey = useMemo(
    () => patternKey(
      model.relation,
      model.visual,
      model.signal,
    ),
    [
      model.relation,
      model.signal,
      model.visual,
    ],
  );

  // Pattern Map is secondary: collapsed by default.
  const [expanded, setExpanded] =
    useState(false);

  const [mastery, setMastery] =
    useState(
      () => readPatternMastery(masteryKey)
    );
  // Word-by-word / task reveal is opt-in. Default is closed.
  const [supportVisible, setSupportVisible] = useState(false);

  useEffect(() => {
    const nextMastery =
      readPatternMastery(masteryKey);

    setMastery(nextMastery);
    setExpanded(false);
    setSupportVisible(false);
  }, [masteryKey, questionKey]);

  useEffect(() => {
    if (correct === undefined) return;
    const marker = `lid.human-learning.attempt.${attemptId || `${questionKey}:${selectedKey || ''}`}`;
    try {
      if (sessionStorage.getItem(marker)) return;
      sessionStorage.setItem(marker, '1');
      const current =
        readPatternMastery(masteryKey);

      const next =
        correct
          ? Math.min(6, current + 1)
          : Math.max(0, current - 1);

      localStorage.setItem(
        `lid.human-learning.pattern-mastery.${masteryKey}`,
        String(next),
      );

      setMastery(next);

      window.dispatchEvent(
        new Event(
          'lid-pattern-mastery-changed'
        )
      );
    } catch {
      // ignore storage errors
    }
  }, [
    attemptId,
    correct,
    masteryKey,
    questionKey,
    selectedKey,
  ]);

  const guidanceLevel = mastery >= 4 ? 'independent' : mastery >= 2 ? 'guided' : 'full';
  const hasResult = Boolean(correctAnswer && correctKey);
  // Only after the learner has answered do we reveal the comparison and proof.
  const showComparison = hasResult && correct === false && Boolean(selectedAnswer);
  // Word-by-word / task are only shown when the learner explicitly opts in.
  const showQuestionMap = supportVisible;
  const showTask = supportVisible;

  return (
    <section
      className={`lid-human-bridge is-${guidanceLevel} ${expanded ? 'is-expanded' : 'is-collapsed'}`}
      aria-label={copy.title}
    >
      <button
        type="button"
        className="lid-human-header"
        aria-expanded={expanded}
        onClick={() => setExpanded(value => !value)}
      >
        <MeaningVisual kind={model.visual} />
        <span className="lid-human-heading">
          <strong>{copy.title}</strong>
          <span className="lid-human-preview">
            <span className="lid-human-preview-signal">
              {model.signal}
            </span>

            <span aria-hidden="true">→</span>

            <span>
              {hasResult
                ? (
                    answerConnection?.answer_evidence
                    || correctAnswer
                    || model.signalMeaning
                  )
                : model.signalMeaning}
            </span>
          </span>
        </span>
        <span
          className="lid-human-level"
          aria-label={copy.subtitle}
          title={`${mastery}/6`}
        >
          <i className={mastery >= 1 ? 'is-on' : ''} />
          <i className={mastery >= 3 ? 'is-on' : ''} />
          <i className={mastery >= 5 ? 'is-on' : ''} />
          <b>{mastery}/6</b>
        </span>
        <span className="lid-human-toggle">{expanded ? copy.hide : copy.show}</span>
      </button>

      {expanded && (
        <div className="lid-human-body">
          {showQuestionMap && (
            <div className="lid-human-chunks">
              {model.chunks.map((chunk, index) => (
                <article className="lid-human-chunk" key={`${chunk.german}-${index}`}>
                  <span className="lid-human-step">{index + 1}</span>
                  <div>
                    <strong lang="de">{chunk.german}</strong>
                    {chunk.translation && language !== 'de' && (
                      <p lang={language} dir={translationDirection}>{chunk.translation}</p>
                    )}
                  </div>
                </article>
              ))}
            </div>
          )}

          {showTask && (
            <article className="lid-human-intent">
              <MeaningVisual kind="task" />
              <div>
                <span className="lid-human-label">{copy.questionPattern}</span>
                <strong>{model.task}</strong>
              </div>
            </article>
          )}

          {(
            model.trapTerms.length > 0
            || model.sharedTerms.length > 0
          ) && (
            <div className="lid-human-signals">
              {model.trapTerms.map(item => (
                <span
                  key={`trap-${item.word}`}
                  className="is-trap"
                >
                  <strong>{item.word}</strong>
                  <small>{item.meaning}</small>
                </span>
              ))}

              {model.sharedTerms.map(item => (
                <span
                  key={`shared-${item.german}`}
                  className="is-shared"
                >
                  <strong>{item.german}</strong>
                  {language !== 'de'
                    && item.meaning
                    && item.meaning !== item.german
                    && (
                      <small>
                        {item.meaning}
                      </small>
                    )}
                </span>
              ))}
            </div>
          )}

          {supportVisible
            && model.wordHelp.length > 0
            && (
              <div className="lid-human-word-help">
                {model.wordHelp.map(item => (
                  <span key={item.german}>
                    <strong>{item.german}</strong>
                    <small>{item.meaning}</small>
                  </span>
                ))}
              </div>
            )}

          {!hasResult && (
            <div className="lid-human-link">
              <div className="lid-human-link-part">
                <span className="lid-human-label">
                  {copy.topic}
                </span>
                <strong>{model.concept}</strong>
              </div>

              <svg
                viewBox="0 0 64 24"
                aria-hidden="true"
              >
                <path d="M2 12h55M49 4l9 8-9 8" />
              </svg>

              <div className="lid-human-link-part is-answer">
                <span className="lid-human-label">
                  {copy.proofAfter}
                </span>
                <span
                  className="lid-human-locked"
                  aria-hidden="true"
                />
              </div>
            </div>
          )}

          {showComparison && (
            <div className="lid-human-compare">
              <article className="is-wrong">
                <span>{incorrectLabel} · {selectedKey}</span>
                <strong>{selectedAnswer}</strong>
                {translatedSelectedAnswer && language !== 'de' && (
                  <p dir={translationDirection}>{translatedSelectedAnswer}</p>
                )}
              </article>
              <article className="is-correct">
                <span>{correctLabel} · {correctKey}</span>
                <strong>{correctAnswer}</strong>
                {translatedCorrectAnswer && language !== 'de' && (
                  <p dir={translationDirection}>{translatedCorrectAnswer}</p>
                )}
              </article>
            </div>
          )}

          {hasResult && (
            <article className="lid-human-proof lid-human-proof-compact">
              <MeaningVisual kind="answer" />
              <div>
                <span className="lid-human-label">{copy.proof}</span>
                <strong>
                  {answerConnection?.translated_complete_fact
                    || translatedCorrectAnswer
                    || answerConnection?.complete_fact
                    || correctAnswer}
                </strong>
                <p>{answerConnection?.reason || copy.followFlow}</p>
              </div>
            </article>
          )}

          {!supportVisible && (
            <button
              type="button"
              className="lid-human-reveal"
              onClick={() => setSupportVisible(true)}
            >
              {copy.show} · {copy.questionPattern}
            </button>
          )}
        </div>
      )}
    </section>
  );
}
